import { Box, ButtonBase, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { CalendarDateService, DateObject } from '@uplift/services';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { Timeslot } from '../types';
import { useStyles } from '../styles';
import clsx from 'clsx';

export type FixedDatePickerProps = {
  timeslots: Timeslot[];
  currentDate: DateObject;
  setCurrentDate: Dispatch<SetStateAction<DateObject>>;
};

const DATE_HASH_FORMAT = 'YYYY-MM-DD';

type DateHashType = {
  [key: string]: true;
};

const createDateHashFromTimeSlots = (timeslots: Timeslot[]) => {
  return timeslots.reduce((r, timeslot) => {
    const keyFormatted = timeslot.format(DATE_HASH_FORMAT);

    return { ...r, [keyFormatted]: true as const };
  }, {} as DateHashType);
};

export const FixedDatePicker: React.VFC<FixedDatePickerProps> = (props) => {
  const { classes } = useStyles();
  const weekdays = CalendarDateService.date.weekdaysMin();
  const today = CalendarDateService.date().startOf('day');
  const thisYear = today.format('YYYY');

  const dateHash = useMemo(() => {
    return createDateHashFromTimeSlots(props.timeslots);
  }, [ props.timeslots ]);

  const dateSelectHandler = useCallback((dateObject: DateObject) => {
    props.setCurrentDate(dateObject);
  }, [ props ]);

  const renderDateCell = useCallback((dateObject: DateObject) => {
    const date = dateObject.date();

    const isHighlighted = dateHash[dateObject.format(DATE_HASH_FORMAT)];
    const isToday = props.currentDate.isSame(dateObject.startOf('day'));

    return (
      <TableCell key={`date-${date}`} className={classes.tableCell}>
        <ButtonBase
          disabled={!isHighlighted}
          className={clsx([ classes.tableButton ], {
            [classes.tableButtonHighlighted]: isHighlighted,
            [classes.tableButtonIsSelected]: isToday
          })}
          onClick={() => dateSelectHandler(dateObject)}
        >
          <Typography color="inherit" variant="body2">
            {date}
          </Typography>
        </ButtonBase>
      </TableCell>
    );
  }, [ classes.tableButton, classes.tableButtonHighlighted, classes.tableButtonIsSelected, classes.tableCell, dateHash, dateSelectHandler, props.currentDate ]);

  const rows = useMemo(() => {
    const todayNextMonth = today.add(1, 'month');
    const fixedLenDates = todayNextMonth.diff(today, 'days');
    const todayWeekday = today.day();
    const nextMonthWeekday = todayNextMonth.day();

    const startBlankSlots = Array.from({ length: todayWeekday }, (_, i) => <TableCell key={`start-blank-${i}`} className={classes.tableCell} />);
    const dateSlots = Array.from({ length: fixedLenDates }, (_, i) => renderDateCell(today.add(i, 'day')));
    const endBlankSlots = Array.from({ length: 7 - nextMonthWeekday }, (_, i) => <TableCell key={`end-blank-${i}`} className={classes.tableCell} />);

    const allSlots = [ ...startBlankSlots, ...dateSlots, ...endBlankSlots ];

    const rowsLength = Math.ceil(allSlots.length / 7);
    const rows = Array.from({ length: rowsLength }, (_, i) => {
      const rowCells = allSlots.slice(i * 7, i * 7 + 7);
      const row = <TableRow key={i}>{rowCells}</TableRow>;

      return row;
    });

    return rows;
  }, [ classes.tableCell, renderDateCell, today ]);

  const monthLabel = useMemo(() => {
    const thisMonth = today.format('MMM');

    // if the date is on the first of the month, then only that month will show on the calendar
    if (today.date() === 1) return thisMonth;
    const nextMonth = today.add(1, 'month').format('MMM');

    return `${thisMonth}–${nextMonth}`;
  }, [ today ]);

  return (
    <>
      <Box px={1} py={1}>
        <Typography>
          <Typography component="span" className={classes.fontWeight600}>{monthLabel}</Typography>
            &nbsp;{thisYear}
        </Typography>
      </Box>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            {weekdays.map((weekday) => {
              return <TableCell key={weekday} className={classes.tableCell}>{weekday[0]}</TableCell>;
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows}
        </TableBody>
      </Table>
    </>
  );
};
